import React, { useState } from 'react';
import dayjs from 'dayjs';
import {
    Dialog,
    DialogContent,
    DialogHeader,
    DialogTitle,
    DialogFooter,
    DialogDescription
} from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Label } from '@/components/ui/label';
import { Input } from '@/components/ui/input';
import {
    Select,
    SelectContent,
    SelectItem,
    SelectTrigger,
    SelectValue
} from '@/components/ui/select';
import { apiPost } from '@/api'; // Using shared api utility
import { Loader2, Calendar } from 'lucide-react';
import { useAuth } from '@/context/useAuth';

interface DateTableWizardProps {
    isOpen: boolean;
    onClose: () => void;
    onSuccess: () => void;
    dataModelId: string;
}

export function DateTableWizard({ isOpen, onClose, onSuccess, dataModelId }: DateTableWizardProps) {
    const [loading, setLoading] = useState(false);
    const { token } = useAuth();

    // Form State
    const [name, setName] = useState('Date Table');
    const [startYear, setStartYear] = useState(dayjs().year() - 2);
    const [endYear, setEndYear] = useState(dayjs().year() + 1);
    const [fiscalStartMonth, setFiscalStartMonth] = useState('1'); // 1 = Jan
    const [fiscalMode, setFiscalMode] = useState('Standard');

    const handleCreate = async () => {
        if (!dataModelId) {
            alert("Data Model ID missing.");
            return;
        }
        setLoading(true);
        try {
            await apiPost(`/datamodels/${dataModelId}/date-table`, token, {
                name,
                start_year: startYear,
                end_year: endYear,
                fiscal_start_month: parseInt(fiscalStartMonth),
                fiscal_mode: fiscalMode
            });
            onSuccess();
            onClose();
        } catch (err: any) {
            console.error(err);
            alert("Failed to create date table: " + (err.message || "Unknown error"));
        } finally {
            setLoading(false);
        }
    };

    return (
        <Dialog open={isOpen} onOpenChange={onClose}>
            <DialogContent className="sm:max-w-[500px] p-6 overflow-hidden border-0 shadow-2xl z-[150] bg-white rounded-xl ">
                <DialogHeader className="mb-4">
                    <div className="flex justify-between items-start">
                        <div>
                            <DialogTitle className="text-xl font-bold text-slate-900">New Date Table</DialogTitle>
                            <DialogDescription className="text-slate-500 mt-1.5 text-sm">
                                Generate a comprehensive date dimension for your data model.
                            </DialogDescription>
                        </div>
                    </div>
                </DialogHeader>

                <div className="space-y-5">
                    <div className="space-y-1.5">
                        <Label className="text-sm font-semibold text-slate-700">Name</Label>
                        <Input
                            value={name}
                            onChange={e => setName(e.target.value)}
                            className="bg-white border-slate-200 focus:border-blue-500 focus:ring-2 focus:ring-blue-100 h-11 rounded-lg transition-all"
                            placeholder="e.g., DimDate"
                        />
                    </div>

                    <div className="grid grid-cols-2 gap-5">
                        <div className="space-y-1.5">
                            <Label className="text-sm font-semibold text-slate-700">Start Year</Label>
                            <Input
                                type="number"
                                value={startYear}
                                onChange={e => setStartYear(parseInt(e.target.value))}
                                className="bg-white border-slate-200 focus:border-blue-500 focus:ring-2 focus:ring-blue-100 h-11 rounded-lg transition-all"
                            />
                        </div>
                        <div className="space-y-1.5">
                            <Label className="text-sm font-semibold text-slate-700">End Year</Label>
                            <Input
                                type="number"
                                value={endYear}
                                onChange={e => setEndYear(parseInt(e.target.value))}
                                className="bg-white border-slate-200 focus:border-blue-500 focus:ring-2 focus:ring-blue-100 h-11 rounded-lg transition-all"
                            />
                        </div>
                    </div>

                    <div className="space-y-1.5">
                        <Label className="text-sm font-semibold text-slate-700">Fiscal Start Month</Label>
                        <Select value={fiscalStartMonth} onValueChange={setFiscalStartMonth}>
                            <SelectTrigger className="bg-white border-slate-200 focus:border-blue-500 focus:ring-2 focus:ring-blue-100 h-11 rounded-lg transition-all">
                                <SelectValue />
                            </SelectTrigger>
                            <SelectContent className="z-[200]">
                                {Array.from({ length: 12 }, (_, i) => i + 1).map(m => (
                                    <SelectItem key={m} value={m.toString()}>
                                        {dayjs().month(m - 1).format('MMMM')}
                                    </SelectItem>
                                ))}
                            </SelectContent>
                        </Select>
                    </div>

                    <div className="space-y-1.5">
                        <Label className="text-sm font-semibold text-slate-700">Fiscal Mode</Label>
                        <Select value={fiscalMode} onValueChange={setFiscalMode}>
                            <SelectTrigger className="bg-white border-slate-200 focus:border-blue-500 focus:ring-2 focus:ring-blue-100 h-11 rounded-lg transition-all">
                                <SelectValue />
                            </SelectTrigger>
                            <SelectContent className="z-[200]">
                                <SelectItem value="Standard">Standard Calendar Function</SelectItem>
                                <SelectItem value="4-4-5">4-4-5 Retail Calendar (Standard Fallback)</SelectItem>
                            </SelectContent>
                        </Select>
                        <p className="text-xs text-slate-400">
                            Determines how fiscal periods are calculated.
                        </p>
                    </div>
                </div>

                <DialogFooter className="mt-8 gap-3 sm:gap-3">
                    <Button
                        variant="outline"
                        onClick={onClose}
                        disabled={loading}
                        className="font-medium text-slate-600 border-slate-200 hover:bg-slate-50 hover:text-slate-800 px-6 h-11 rounded-lg"
                    >
                        Cancel
                    </Button>
                    <Button
                        onClick={handleCreate}
                        disabled={loading}
                        className="bg-blue-500 hover:bg-blue-600 text-white font-medium shadow-md shadow-blue-200 px-6 h-11 rounded-lg transition-all"
                    >
                        {loading ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : <Calendar className="w-4 h-4 mr-2" />}
                        Generate
                    </Button>
                </DialogFooter>
            </DialogContent>
        </Dialog>
    );
}
